/**
 * A shared book's read view (part 38c; decisions 75, 76, 59; tables NV and CC; cockatrice-ui `EditorView.jsx` at
 * Read): the bar (back to Shared with me, the book and its author, the access badge, Notes), the access banner (the
 * invitee's role, what they can do, the end date), the chapter rail, the chapter's text read only, and the notes panel
 * (This chapter and Notebook; the notes of their role and their own thread; writing, replying, editing and deleting
 * their own where the claim allows). The design's Suggest and Edit don't exist (decision 75: nobody else changes the
 * text): the access is said as text. Everything shown from the copy and the notes is text, never markup (decision 59).
 */
import { createEffect, createMemo, createSignal, on, untrack } from 'solid-js';
import type { Accessor } from 'solid-js';
import { countsOf, isOwn, threadsOf, whoWrote } from '../shared/book.ts';
import type { SharedBook, Thread } from '../shared/book.ts';
import { mayPost, newNoteGroup } from '../shared/shares.ts';
import type { Draft, NoteOutcome } from '../shared/shares.ts';
import type { CachedClaim, HeldNote } from '../shared/store.ts';
import { cutRuns, shownChapter } from '../shared/text.ts';
import type { ShownBlock, ShownChapter } from '../shared/text.ts';
import type { ChapterPlaces } from '@cockatrice/core';
import { Announcer } from './announce.ts';
import { dateWords } from './account-sheet.ts';
import { each, el, when, whenSome } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import { ExportMenu } from './export-menu.ts';
import { openAccountSettings } from './home.ts';
import { When } from './notes.ts';
import type { Shell } from './shell.ts';
import type { RelayError } from '../account/client.ts';
import { readingFragment } from '../shell/route.ts';
import Badge from '../ui/Badge.ts';
import Banner from '../ui/Banner.ts';
import Button from '../ui/Button.ts';
import ChapterList, { ChapterItem } from '../ui/ChapterList.ts';
import IconButton from '../ui/IconButton.ts';
import InlineConfirm from '../ui/InlineConfirm.ts';
import UiManuscript from '../ui/Manuscript.ts';
import NoteHighlight from '../ui/NoteHighlight.ts';
import Select from '../ui/Select.ts';
import Tabs from '../ui/Tabs.ts';
import Textarea from '../ui/Textarea.ts';
import { component } from '../ui/solid.ts';

/** A visible range of the open chapter (decision 76's anchor space). */
interface Range {
  readonly from: number;
  readonly to: number;
}

/** NO1: a passage chosen for a note: its range, quote and context, as the note's anchor takes them. */
type Passage = ReturnType<ChapterPlaces['placeAt']>;

/** A thread as the panel lists it: with its claim's invite and the visible text its passage is in ('' in the Notebook). */
interface Listed {
  readonly thread: Thread;
  readonly invite: string;
  readonly visible: string;
}

/** The open chapter shown, with its number in the book and its title. */
type Opened = ShownChapter & { readonly number: number; readonly title: string };

/** The claim the address names, as held; null when it isn't (gone, or not this account's). */
function claimOf(shell: Shell): CachedClaim | null {
  const invite = shell.ui.reading();
  return shell.shared.claims().find((claim) => claim.invite === invite) ?? null;
}

/** A run's start in the visible text (its `data-from`), or null for an element that isn't a run. */
function startOf(run: Element | null | undefined): number | null {
  const from = run instanceof HTMLElement ? Number(run.dataset['from']) : Number.NaN;
  return Number.isSafeInteger(from) ? from : null;
}

/**
 * The visible offset a selection's end (`node`, `offset`) stands at: in a run's text, its start and the offset; at an
 * element (a whole run or block chosen, as a triple click or a test's selection gives), the start of the first run at
 * or after the child it names, or the end of its last run when it names none; null outside the text.
 */
function offsetOf(node: Node, offset: number): number | null {
  if (node.nodeType === Node.TEXT_NODE) {
    const from = startOf(node.parentElement?.closest('[data-from]'));
    return from === null ? null : from + offset;
  }
  if (!(node instanceof Element)) return null;
  const child = node.childNodes[offset];
  if (child instanceof Element) return startOf(child.matches('[data-from]') ? child : child.querySelector('[data-from]'));
  if (child !== undefined) return startOf(child.parentElement?.closest('[data-from]'));
  const runs = node.matches('[data-from]') ? [node] : [...node.querySelectorAll('[data-from]')];
  const last = runs.at(-1);
  // A run found by its `data-from` holds the whole number this view wrote there.
  return last === undefined ? null : Number(last.getAttribute('data-from')) + last.textContent.length;
}

/** NO1: the visible range the page's selection covers in `article`, or null (nothing chosen, or outside the text). */
function chosenIn(article: HTMLElement): Range | null {
  const selection = window.getSelection();
  if (selection === null || selection.isCollapsed || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!article.contains(range.commonAncestorContainer)) return null;
  const from = offsetOf(range.startContainer, range.startOffset);
  const to = offsetOf(range.endContainer, range.endOffset);
  return from === null || to === null || to <= from ? null : { from, to };
}

/** A block of the chapter: its runs, each passage covering it marked. */
function Block(block: ShownBlock, marks: () => readonly { readonly range: Range }[], top: number): Child {
  // Under the chapter's title (h1), the chapter's highest heading level is h2, so no level is skipped.
  const tag = block.kind === 'heading' ? `h${String(Math.min(6, Math.max(2, block.level - top + 2)))}` : 'p';
  return el(tag, { class: block.kind === 'centered' ? 'rd-centered' : undefined }, () =>
    cutRuns(
      block.runs,
      marks().map((mark) => mark.range),
    ).map(({ run, covered }) => {
      const classes = [run.marks.emphasis ? 'rd-em' : '', run.marks.strong ? 'rd-strong' : '', run.marks.smallCaps ? 'sc' : ''].filter((name) => name !== '').join(' ');
      const span = el('span', { class: classes === '' ? undefined : classes, 'data-from': String(run.from) }, run.text);
      if (covered.length === 0) return span;
      // The design marks every invitee's passage, their own too, in plum (apart from the blue of a selection).
      return component(NoteHighlight, { kind: 'editor', class: 'rd-mark', children: span });
    }),
  );
}

/** Who wrote a note, in words. */
function whoWords(shell: Shell, note: HeldNote, invite: string): string {
  const who = whoWrote(note, invite);
  if (who.kind === 'you') return shell.t('read.you');
  if (who.kind === 'writer') return shell.t('read.writer');
  return shell.t(`read.reader.${who.group}`);
}

/** NV: a note outcome's words for the live region and the panel. */
function outcomeWords(shell: Shell, outcome: Exclude<NoteOutcome, { readonly kind: 'done' }>): string {
  const { t } = shell;
  if (outcome.kind === 'failed' || outcome.kind === 'not-allowed') return t('read.noteFailed');
  const key = NOTE_WORDS[outcome.error];
  return t(key ?? 'read.noteFailed');
}

/** The relay's refusals of a note said in their own words (IP7, IP9); every other one as a failure to send. */
const NOTE_WORDS: Partial<Readonly<Record<RelayError, 'read.rateLimited' | 'read.notesFull' | 'read.notesOff'>>> = { 'rate-limited': 'read.rateLimited', 'share-full': 'read.notesFull', 'notes-off': 'read.notesOff' };

/** The read view's state. */
interface Reading {
  readonly shell: Shell;
  readonly claim: Accessor<CachedClaim | null>;
  readonly book: Accessor<SharedBook | null>;
  readonly chapter: Accessor<string | null>;
  readonly setChapter: (id: string) => void;
  readonly shown: Accessor<Opened | null>;
  readonly passage: Accessor<Passage | null>;
  readonly setPassage: (passage: Passage | null) => void;
  readonly notesOpen: Accessor<boolean>;
  readonly setNotesOpen: (open: boolean) => void;
  readonly tab: Accessor<'chapter' | 'notebook'>;
  readonly setTab: (tab: 'chapter' | 'notebook') => void;
  readonly news: Accessor<string>;
  readonly say: (words: string) => void;
}

/** The bar: back to Shared with me, the book and its author, the access badge, Notes. */
function Bar(reading: Reading): Child {
  const { shell, claim, book, notesOpen, setNotesOpen } = reading;
  const { t } = shell;
  const count = (): number => claim()?.held.filter((note) => note.note.kind === 'note').length ?? 0;
  return el(
    'header',
    { class: 'rd-bar' },
    // On a phone the design's back is its mark alone, its words the button's name.
    when(
      () => shell.mode() === 'narrow',
      () => component(IconButton, { icon: 'back', label: t('read.back'), href: '#/shared', class: 'rd-back' }),
      () => component(Button, { variant: 'ghost', iconStart: 'back', href: '#/shared', class: 'rd-back', children: t('read.back') }),
    ),
    el(
      'div',
      { class: 'wr-crumbs' },
      el('span', { class: 'wr-crumbs__book' }, () => book()?.metadata?.title ?? t('shared.untitled')),
      whenSome(
        () => {
          const author = book()?.metadata?.author ?? '';
          return author === '' ? null : author;
        },
        (author) => [el('span', { class: 'wr-crumbs__sep', 'aria-hidden': 'true' }, '·'), el('span', { class: 'wr-crumbs__chapter' }, () => t('read.by', { author: author() }))],
      ),
    ),
    el(
      'div',
      { class: 'rd-tools' },
      // The badge is the wide bar's; a phone has the banner's words alone (the design's).
      whenSome(
        () => (shell.mode() === 'narrow' ? null : claim()),
        (held) => component(Badge, { size: 'lg', icon: 'eye', class: 'rd-badge', get children() { return t('read.badge', { notes: t(`read.notes.${held().notes}`) }); } }),
      ),
      // GP6, decision 75: Export only when the writer allowed it (it hides the buttons only, IN16).
      when(
        () => claim()?.export === true,
        () => ExportMenu(shell, { class: 'rd-export', variant: 'ghost', size: 'sm', label: t('read.export'), shared: true }),
      ),
      component(Button, {
        variant: 'ghost',
        size: 'sm',
        iconStart: 'notes',
        class: 'rd-notes-button',
        get 'aria-pressed'() {
          return notesOpen() ? 'true' : 'false';
        },
        'aria-controls': 'rd-notes',
        onClick: () => {
          setNotesOpen(!notesOpen());
        },
        children: [t('read.notesButton'), el('span', { class: 'sh-count' }, () => ` · ${String(count())}`)],
      }),
      // The design's bar ends with the account (its settings section), as the home page's header does.
      component(IconButton, { icon: 'user', label: t('home.account'), outlined: true, class: 'open-account', onClick: () => { openAccountSettings(shell); } }),
    ),
  );
}

/** How many of the book's chapters aren't on this device yet, said under the rail or the phone's list. */
function Missing(reading: Reading): Child {
  const { shell, book } = reading;
  return whenSome(
    () => {
      const missing = book()?.missing ?? 0;
      return missing === 0 ? null : missing;
    },
    (missing) => el('p', { class: 'sh-help rd-missing' }, () => shell.t('read.missing', { count: missing() })),
  );
}

/** A narrow screen has no rail: its chapters come from this list under the banner, with the count of those not here. */
function ChapterPick(reading: Reading): Child {
  const { shell, book } = reading;
  const { t } = shell;
  return whenSome(
    () => {
      const found = book();
      return shell.mode() === 'narrow' && found !== null && (found.chapters.length > 0 || found.missing > 0) ? found : null;
    },
    (found) =>
      // A named region, so the list sits inside a landmark (axe's `region`).
      el(
        'div',
        { class: 'rd-chapter-pick', role: 'region', 'aria-label': t('read.chapters') },
        when(
          () => found().chapters.length > 0,
          () =>
            component(Select, {
              'aria-label': t('read.chapters'),
              fullWidth: true,
              get options() {
                return found().chapters.map((entry, index) => ({ value: entry.id, label: `${String(index + 1)} · ${entry.title === '' ? t('chapter.untitled') : entry.title}` }));
              },
              get value() {
                return reading.chapter() ?? '';
              },
              onChange: (value: string) => {
                reading.setChapter(value);
              },
            }),
        ),
        Missing(reading),
      ),
  );
}

/** The access banner (IN11's end said here too). */
function AccessBanner(reading: Reading): Child {
  const { shell, claim } = reading;
  const { t } = shell;
  return whenSome(claim, (held) =>
    el(
      'div',
      // A named region, so the access said here is inside a landmark (axe's `region`).
      { class: 'rd-banner', role: 'region', 'aria-labelledby': 'rd-access' },
      component(Banner, {
        tone: 'neutral',
        icon: false,
        strip: true,
        title: el('span', { id: 'rd-access' }, () => t(`read.youAre.${held().role}`)),
        children: [
          el('span', { class: 'rd-can' }, () => t(`read.can.${held().notes}`)),
          whenSome(
            () => held().endsAt,
            (ends) => el('span', { class: 'rd-ends' }, () => t('shared.ends', { date: dateWords(shell, ends()) })),
          ),
        ],
      }),
    ),
  );
}

/** The chapter rail: the book's chapters held, each with its notes' count, and how many aren't here yet. */
function Rail(reading: Reading): Child {
  const { shell, claim, book, chapter, setChapter } = reading;
  const { t } = shell;
  const counts = createMemo(() => {
    const held = claim();
    return held === null ? new Map<string, number>() : countsOf(held);
  });
  // A region named by its label, so the count of chapters not here yet (under the list) is inside a landmark too.
  return el(
    'div',
    { class: 'rd-rail', role: 'region', 'aria-labelledby': 'rd-rail-label' },
    el('span', { id: 'rd-rail-label', class: 'wr-rail__label' }, t('read.chapters')),
    component(ChapterList, {
      class: 'rd-chapters',
      label: t('read.chapters'),
      footer: null,
      children: each(
        () => book()?.chapters ?? [],
        (entry, index) =>
          component(ChapterItem, {
            class: 'rd-chapter',
            get number() {
              return index() + 1;
            },
            title: entry.title === '' ? t('chapter.untitled') : entry.title,
            get words() {
              const n = counts().get(entry.id) ?? 0;
              return n === 0 ? undefined : n;
            },
            get current() {
              return chapter() === entry.id;
            },
            // The chapter is a place in this book's address (a new tab opens the book); here it opens in place.
            get href() {
              return readingFragment(claim()?.invite ?? '');
            },
            onSelect: (event: MouseEvent) => {
              event.preventDefault();
              setChapter(entry.id);
            },
          }),
      ),
    }),
    Missing(reading),
  );
}

/** The chapter's text, read only, its notes' passages marked; a selection offers Add note and Copy. */
function Manuscript(reading: Reading): Child {
  const { shell, claim, book, chapter, shown, passage, setPassage, setNotesOpen, setTab } = reading;
  const { t } = shell;
  // NO1: the selection's passage as a note would take it, and its text (for Copy), made as it is chosen.
  const [chosen, setChosen] = createSignal<{ readonly place: Passage; readonly text: string } | null>(null);
  // A selection belongs to its chapter: another chapter opened leaves nothing to add a note to or copy.
  createEffect(on(chapter, () => setChosen(null), { defer: true }));
  // And a text gone (the book no longer shared, CC1) takes its selection with it.
  createEffect(
    on(shown, (opened) => {
      if (opened === null) setChosen(null);
    }),
  );
  const marks = createMemo(() => {
    const held = claim();
    const opened = shown();
    const found = book();
    const id = chapter();
    if (held === null || opened === null || found === null || id === null) return [];
    const ranges = threadsOf(held, found, id, opened.visible.length).flatMap((thread) => (thread.range === null ? [] : [{ range: thread.range }]));
    const chosenNow = passage();
    return chosenNow === null ? ranges : [...ranges, { range: chosenNow.range }];
  });
  return el(
    'section',
    { class: 'rd-text', 'aria-label': t('read.manuscript') },
    whenSome(
      shown,
      (opened) => {
        const look = (event: { readonly currentTarget: HTMLElement }): void => {
          const range = chosenIn(event.currentTarget);
          setChosen(range === null ? null : { place: opened().places().placeAt(range), text: opened().visible.slice(range.from, range.to) });
        };
        return component(UiManuscript, {
          class: 'rd-manuscript',
          get kicker() {
            return t('read.kicker', { number: opened().number });
          },
          get title() {
            return opened().title === '' ? t('chapter.untitled') : opened().title;
          },
          titleId: 'read-title',
          titleFocusable: true,
          onMouseUp: look,
          onKeyUp: look,
          onTouchEnd: look,
          get children() {
            const top = Math.min(...opened().blocks.map((block) => (block.kind === 'heading' ? block.level : 6)));
            return opened().blocks.map((block) => Block(block, marks, top));
          },
        });
      },
      // The page's heading (and the skip link's target) when there is no text to read.
      () => el('h1', { id: 'read-title', tabIndex: -1, class: 'lib-empty rd-empty' }, () => (claim() === null ? t('read.gone') : t('read.empty'))),
    ),
    whenSome(chosen, (selected) =>
      el(
        'div',
        { class: 'sh-selbar', role: 'toolbar', 'aria-label': t('read.selection') },
        when(
          () => {
            const held = claim();
            return held !== null && newNoteGroup(held) !== null;
          },
          () =>
            component(Button, {
              variant: 'ghost',
              size: 'sm',
              iconStart: 'comment',
              class: 'rd-add-note',
              onClick: () => {
                setPassage(selected().place);
                setTab('chapter');
                setNotesOpen(true);
                setChosen(null);
                queueMicrotask(() => document.getElementById('rd-composer')?.focus());
              },
              children: t('read.addNote'),
            }),
        ),
        component(Button, {
          variant: 'ghost',
          size: 'sm',
          class: 'rd-copy',
          onClick: () => {
            void shell.copy(selected().text);
          },
          children: t('read.copy'),
        }),
      ),
    ),
  );
}

/** A thread's card: who and when, its passage, what it says, its replies, and what the invitee may do with it. */
function ThreadCard(reading: Reading, entry: Listed): Child {
  const { shell, claim, say } = reading;
  const { t } = shell;
  const { thread, invite, visible } = entry;
  const [replying, setReplying] = createSignal(false);
  const [editing, setEditing] = createSignal<string | null>(null);
  const [words, setWords] = createSignal('');
  const [error, setError] = createSignal('');
  const quote = (): string => {
    const first = thread.first.note;
    if (first.kind !== 'note' || thread.range === null) return '';
    if (first.anchor !== null && first.anchor.quote !== '') return first.anchor.quote.trim();
    return visible.slice(thread.range.from, thread.range.to).trim();
  };
  const done = (outcome: NoteOutcome, success: string): boolean => {
    if (outcome.kind === 'done') {
      setError('');
      say(success);
      return true;
    }
    const message = outcomeWords(shell, outcome);
    setError(message);
    say(message);
    return false;
  };
  const NoteBody = (note: HeldNote): Child =>
    when(
      () => editing() === note.id,
      () =>
        el(
          'form',
          {
            class: 'rd-edit',
            onSubmit: (event: SubmitEvent) => {
              event.preventDefault();
              const text = words().trim();
              if (text === '') return;
              const draft: Draft = note.note.kind === 'reply' ? { kind: 'reply', to: note.note.to, text } : { ...note.note, text };
              void shell.shared.post(invite, draft, note.id).then((outcome) => {
                if (done(outcome, t('read.saved'))) setEditing(null);
              });
            },
          },
          el('label', { class: 'visually-hidden', for: `rd-edit-${note.id}` }, t('read.edit')),
          component(Textarea, { id: `rd-edit-${note.id}`, rows: 3, defaultValue: note.note.text, onChange: (value: string) => setWords(value) }),
          el(
            'div',
            { class: 'sh-note__actions' },
            component(Button, { variant: 'link', onClick: () => setEditing(null), children: t('read.cancel') }),
            component(Button, { variant: 'primary', size: 'sm', type: 'submit', children: t('read.save') }),
          ),
        ),
      () => el('p', { class: 'sh-note__body' }, note.note.text),
    );
  const ReplyButton = (): Child => when(() => canReply() && !replying(), () => component(Button, { variant: 'link', class: 'rd-reply', onClick: () => setReplying(true), children: t('read.reply') }));
  /**
   * A note's actions in one row (the design's): Edit and Delete on the invitee's own, and Reply on the thread's first.
   * Delete asks in place first (the library's InlineConfirm).
   */
  const OwnActions = (note: HeldNote, reply: boolean): Child =>
    when(
      () => isOwn(note, invite) && editing() !== note.id,
      () =>
        el(
          'div',
          { class: 'sh-note__actions' },
          component(Button, {
            variant: 'link',
            class: 'rd-edit-start',
            onClick: () => {
              setWords(note.note.text);
              setEditing(note.id);
            },
            children: t('read.edit'),
          }),
          component(InlineConfirm, {
            label: t('read.delete'),
            message: t('read.deleteAsk'),
            cancelLabel: t('read.cancel'),
            triggerClass: 'rd-delete ui-inline-confirm__trigger ui-inline-confirm__trigger--danger',
            onConfirm: () => {
              void shell.shared.remove(invite, note.id).then((outcome) => {
                done(outcome, t('read.deleted'));
              });
            },
          }),
          reply ? ReplyButton() : null,
        ),
      () => (reply ? when(() => editing() !== note.id && canReply() && !replying(), () => el('div', { class: 'sh-note__actions' }, ReplyButton())) : null),
    );
  // The design's: Reply on a thread with someone else in it (another's note, or one's own once answered).
  const canReply = (): boolean => {
    const held = claim();
    return held !== null && mayPost(held, thread.first.group) && (!isOwn(thread.first, held.invite) || thread.replies.length > 0);
  };
  return el(
    'li',
    { class: 'sh-note' },
    el('div', { class: 'sh-note__meta' }, el('strong', {}, whoWords(shell, thread.first, invite)), When(shell, () => thread.first.at)),
    when(
      () => quote() !== '',
      () => el('blockquote', { class: 'sh-quote' }, () => `“${quote()}”`),
    ),
    NoteBody(thread.first),
    ...thread.replies.map((reply) =>
      el(
        'div',
        { class: 'sh-reply' },
        el('div', { class: 'sh-note__meta' }, el('strong', {}, whoWords(shell, reply, invite)), When(shell, () => reply.at)),
        NoteBody(reply),
        OwnActions(reply, false),
      ),
    ),
    OwnActions(thread.first, true),
    when(replying, () =>
      el(
        'form',
        {
          class: 'sh-reply-composer',
          onSubmit: (event: SubmitEvent) => {
            event.preventDefault();
            const text = words().trim();
            if (text === '') return;
            void shell.shared.post(invite, { kind: 'reply', to: thread.first.id, text }).then((outcome) => {
              if (done(outcome, t('read.posted'))) {
                setReplying(false);
                setWords('');
              }
            });
          },
        },
        el('label', { class: 'visually-hidden', for: `rd-reply-${thread.first.id}` }, t('read.replyLabel')),
        component(Textarea, { id: `rd-reply-${thread.first.id}`, rows: 2, onChange: (value: string) => setWords(value) }),
        el(
          'div',
          { class: 'sh-note__actions' },
          component(Button, { variant: 'link', onClick: () => setReplying(false), children: t('read.cancel') }),
          component(Button, { variant: 'primary', size: 'sm', type: 'submit', children: t('read.reply') }),
        ),
      ),
    ),
    when(
      () => error() !== '',
      () => el('p', { class: 'ac-error', role: 'alert' }, error),
    ),
  );
}

/** The notes panel: This chapter and Notebook, the threads, and the composer where the claim may post. */
function Panel(reading: Reading): Child {
  const { shell, claim, book, chapter, shown, passage, setPassage, notesOpen, setNotesOpen, tab, setTab, say } = reading;
  const { t } = shell;
  const [text, setText] = createSignal('');
  const [error, setError] = createSignal('');
  const listed = (): readonly Listed[] => {
    const held = claim();
    const found = book();
    if (held === null || found === null) return [];
    const of = (threads: readonly Thread[], visible: string): Listed[] => threads.map((thread) => ({ thread, invite: held.invite, visible }));
    if (tab() === 'notebook') return of(threadsOf(held, found, null, 0), '');
    const id = chapter();
    const opened = shown();
    return id === null || opened === null ? [] : of(threadsOf(held, found, id, opened.visible.length), opened.visible);
  };
  // A thread whose notes are as they were keeps its card (and what the card says: a refusal, a form being typed in),
  // though every note outcome reads the claims again.
  const keyOf = ({ thread, invite }: Listed): string => JSON.stringify([invite, thread.first.id, thread.first.seq, thread.range, thread.replies.map((reply) => [reply.id, reply.seq])]);
  const threads = createMemo((previous: readonly Listed[]): readonly Listed[] => {
    const kept = new Map(previous.map((entry) => [keyOf(entry), entry]));
    return listed().map((entry) => kept.get(keyOf(entry)) ?? entry);
  }, []);
  // NV: where the invitee's next note goes, with their role (named in who sees it); null where they post nothing.
  const group = (): { readonly where: NonNullable<ReturnType<typeof newNoteGroup>>; readonly role: CachedClaim['role'] } | null => {
    const held = claim();
    const where = held === null ? null : newNoteGroup(held);
    return held === null || where === null ? null : { where, role: held.role };
  };
  const post = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    const held = claim();
    const words = text().trim();
    if (held === null || words === '') return;
    const draft: Draft =
      tab() === 'notebook'
        ? { kind: 'note', chapter: null, anchor: null, title: '', section: '', text: words }
        : { kind: 'note', chapter: chapter(), anchor: passage(), title: '', section: '', text: words };
    const outcome = await shell.shared.post(held.invite, draft);
    if (outcome.kind === 'done') {
      setText('');
      setError('');
      setPassage(null);
      say(t('read.posted'));
      return;
    }
    const message = outcomeWords(shell, outcome);
    setError(message);
    say(message);
  };
  const notebookCount = (): number => {
    const held = claim();
    const found = book();
    return held === null || found === null ? 0 : threadsOf(held, found, null, 0).length;
  };
  return el(
    'aside',
    { id: 'rd-notes', class: 'sh-notes-panel rd-notes', 'aria-label': t('read.notesButton'), hidden: () => !notesOpen() },
    el(
      'div',
      { class: 'sh-sheet-handle' },
      el('span', { 'aria-hidden': 'true' }),
      component(IconButton, { icon: 'close', label: t('read.closeNotes'), class: 'rd-close-notes', onClick: () => { setNotesOpen(false); } }),
    ),
    // Arrow keys move and choose (Home and End too); the panel below is the chosen tab's (ids rd-tab-chapter, -notebook).
    component(Tabs, {
      id: 'rd',
      class: 'rd-tabs',
      label: t('read.notesButton'),
      panelId: 'rd-notes-list',
      tabs: [
        { value: 'chapter', label: t('read.tab.chapter') },
        {
          value: 'notebook',
          label: t('read.tab.notebook'),
          get count() {
            return t('read.notebookCount', { count: notebookCount() });
          },
        },
      ],
      get value() {
        return tab();
      },
      onChange: (value: string) => {
        setTab(value === 'notebook' ? 'notebook' : 'chapter');
      },
    }),
    el(
      'div',
      { class: 'sh-notes-scroll', id: 'rd-notes-list', role: 'tabpanel', 'aria-labelledby': () => `rd-tab-${tab()}` },
      when(
        () => threads().length === 0,
        () => el('p', { class: 'sh-help' }, t('read.noNotes')),
        () => el('ul', { class: 'sh-note-list' }, each(threads, (entry) => ThreadCard(reading, entry))),
      ),
      el('p', { class: 'sh-foot-note' }, t('read.private')),
    ),
    whenSome(group, (where) =>
      el(
        'form',
        { class: 'sh-composer rd-composer', onSubmit: (event: SubmitEvent) => void post(event) },
        whenSome(passage, (chosen) => el('blockquote', { class: 'sh-quote' }, () => `“${chosen().quote}”`)),
        el('label', { class: 'visually-hidden', for: 'rd-composer' }, t('read.composer')),
        component(Textarea, {
          id: 'rd-composer',
          rows: 2,
          get value() {
            return text();
          },
          'aria-describedby': 'rd-composer-who',
          get placeholder() {
            return tab() === 'notebook' ? t('read.placeholderNotebook') : t('read.placeholder');
          },
          onChange: (value: string) => setText(value),
        }),
        el('p', { id: 'rd-composer-who', class: 'sh-help' }, () => (where().where === 'person' ? t('read.toWriter') : t('read.toGroup', { group: t(`read.group.${where().role}`) }))),
        when(
          () => error() !== '',
          () => el('p', { class: 'ac-error' }, error),
        ),
        el(
          'div',
          { class: 'sh-composer__row' },
          el('span', {}),
          component(Button, {
            variant: 'primary',
            size: 'sm',
            type: 'submit',
            class: 'rd-post',
            get fullWidth() {
              return shell.mode() === 'narrow';
            },
            children: t('read.post'),
          }),
        ),
      ),
    ),
  );
}

/** The read view (route `reading`): the claim the address names, its book opened from the cache. */
export function ReadingPage(shell: Shell, statuses: () => Child): Child {
  const { t } = shell;
  const claim = createMemo(() => claimOf(shell));
  const [book, setBook] = createSignal<SharedBook | null>(null);
  const [chapter, setChapter] = createSignal<string | null>(null);
  const [passage, setPassage] = createSignal<Passage | null>(null);
  const [notesOpen, setNotesOpen] = createSignal(false);
  const [tab, setTab] = createSignal<'chapter' | 'notebook'>('chapter');
  const [news, setNews] = createSignal('');
  // R5: the page entered takes the focus on its chapter's title once the book has opened (the route's own focus found
  // only the way back, the book being opened then).
  let entering = true;
  // CC: each claim as a check leaves it, opened again (the copies and notes it holds may have changed).
  createEffect(
    on(claim, (held) => {
      if (held === null) {
        setBook(null);
        return;
      }
      void shell.shared.book(held.invite).then((opened) => {
        setBook(opened);
        const current = untrack(chapter);
        if (opened !== null && (current === null || !opened.chapters.some((entry) => entry.id === current))) setChapter(opened.chapters[0]?.id ?? null);
        if (entering && opened !== null) {
          entering = false;
          const active = document.activeElement;
          if (active === null || active === document.body || active.classList.contains('rd-back')) queueMicrotask(() => document.getElementById('read-title')?.focus());
        }
      });
    }),
  );
  const shown = createMemo((): Opened | null => {
    const found = book();
    const index = found === null ? -1 : found.chapters.findIndex((entry) => entry.id === chapter());
    const entry = found?.chapters[index];
    return entry === undefined ? null : { ...shownChapter(entry.text), number: index + 1, title: entry.title };
  });
  const reading: Reading = {
    shell,
    claim,
    book,
    chapter,
    setChapter: (id) => {
      setChapter(id);
      setPassage(null);
      queueMicrotask(() => document.getElementById('read-title')?.focus());
    },
    shown,
    passage,
    setPassage,
    notesOpen,
    setNotesOpen,
    tab,
    setTab,
    news,
    say: (words) => {
      setNews('');
      setNews(words);
    },
  };
  return el(
    'div',
    { class: () => `rd ${shell.mode()}` },
    el('a', { class: 'skip', href: '#read-title' }, t('read.manuscript')),
    Announcer('reading', news),
    Bar(reading),
    statuses(),
    AccessBanner(reading),
    ChapterPick(reading),
    el(
      'div',
      { class: 'sh-notes-body rd-body', 'data-sheet': () => (notesOpen() ? 'open' : 'closed') },
      // A phone has no rail (the chapters' list under the banner stands in for it).
      when(
        () => shell.mode() !== 'narrow',
        () => Rail(reading),
      ),
      el('main', { class: 'rd-main' }, Manuscript(reading)),
      el('div', { class: 'sh-sheet-scrim', 'aria-hidden': 'true', onClick: () => { setNotesOpen(false); } }),
      Panel(reading),
    ),
  );
}
